import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Loader2, TrendingUp, TrendingDown, Book, Gift, ShoppingCart, Store,
  Plus, Trash2, Sparkles, AlertCircle, Target, Lightbulb, RefreshCw,
  Pencil, Check, X,
} from "lucide-react";
import { adminGet, getToken } from "@/lib/adminApi";
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip,
  CartesianGrid, Line, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_TYPES = [
  { id: "purchase", label: "Acquisto autore", icon: Book, color: "#B23A48" },
  { id: "gift", label: "Regalo", icon: Gift, color: "#E89B6C" },
  { id: "sale_amazon", label: "Vendita Amazon", icon: ShoppingCart, color: "#0E7C8F" },
  { id: "sale_direct", label: "Vendita diretta", icon: Store, color: "#0B7C5C" },
];

function fmtEuro(n) { return `€ ${Number(n || 0).toFixed(2)}`; }
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

const PRIORITY = {
  high: { color: "#B23A48", label: "Alta priorità", icon: AlertCircle },
  medium: { color: "#E89B6C", label: "Media priorità", icon: Target },
  low: { color: "#0E7C8F", label: "Bonus", icon: Lightbulb },
};

export default function LedgerTab() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({
    type: "purchase", quantity: 1, unit_price: "", unit_cost: "", entry_date: new Date().toISOString().slice(0,10), notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        adminGet("/admin/ledger/summary"),
        adminGet("/admin/ledger/entries"),
      ]);
      setSummary(s);
      setEntries(e.items || []);
    } catch (err) {
      toast.error("Errore caricamento contabilità");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quantity || form.quantity < 1) {
      toast.error("Inserisci quantità"); return;
    }
    setSubmitting(true);
    try {
      const body = {
        type: form.type,
        quantity: Number(form.quantity),
        entry_date: form.entry_date || null,
        notes: form.notes || null,
      };
      if (form.unit_price) body.unit_price = Number(form.unit_price);
      if (form.unit_cost) body.unit_cost = Number(form.unit_cost);
      await axios.post(`${API}/admin/ledger/entry`, body, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("Movimento aggiunto");
      setForm({ ...form, quantity: 1, notes: "" });
      load();
    } catch { toast.error("Errore inserimento"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo movimento?")) return;
    try {
      await axios.delete(`${API}/admin/ledger/entry/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const updateEntry = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/ledger/entry/${id}`, patch, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("Movimento aggiornato");
      load();
      return true;
    } catch (err) {
      toast.error("Errore aggiornamento");
      return false;
    }
  };

  const pieData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: "Venduti", value: summary.books.sold_total, color: "#0B7C5C" },
      { name: "Conto vendita", value: summary.books.consignment_total, color: "#0E7C8F" },
      { name: "Regalati", value: summary.books.gifted, color: "#E89B6C" },
      { name: "Residui magazzino", value: summary.books.inventory_remaining, color: "#3A4F6B" },
    ].filter(d => d.value > 0);
  }, [summary]);

  if (loading || !summary) {
    return <div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-ledger-tab">
      {/* Books breakdown - 4 cards */}
      <section>
        <h2 className="font-serif-display text-2xl text-[#0B192C] mb-4">Bilancio Libri</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Autori acquistati" value={summary.books.author_purchased} icon={Book} color="#B23A48" testid="stat-purchased" />
          <StatCard label="Regalati" value={summary.books.gifted} icon={Gift} color="#E89B6C" testid="stat-gifted" />
          <StatCard label="Venduti" value={summary.books.sold_total} sublabel={`${summary.books.sold_amazon} Amazon · ${summary.books.sold_direct} diretti`} icon={ShoppingCart} color="#0B7C5C" testid="stat-sold" />
          <StatCard label="In conto vendita" value={summary.books.consignment_total} sublabel={`Richieste pending: ${summary.books.pending_requests}`} icon={Store} color="#0E7C8F" testid="stat-consignment" />
        </div>
      </section>

      {/* Financials */}
      <section>
        <h2 className="font-serif-display text-2xl text-[#0B192C] mb-4">Contabilità</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E5E1DA] p-6" data-testid="fin-cost">
            <div className="flex items-center justify-between mb-3">
              <TrendingDown className="text-[#B23A48]" size={20} />
              <span className="text-xs uppercase tracking-[0.15em] text-[#5A6472]">Spesa Totale</span>
            </div>
            <p className="text-3xl font-mono font-bold text-[#B23A48]">{fmtEuro(summary.financials.cost)}</p>
            <p className="text-xs text-[#5A6472] mt-2">
              {summary.books.author_purchased} libri acquistati · costo medio {fmtEuro(summary.books.author_purchased ? summary.financials.cost / summary.books.author_purchased : summary.financials.default_unit_cost)}/copia
            </p>
          </div>
          <div className="bg-white border border-[#E5E1DA] p-6" data-testid="fin-revenue">
            <div className="flex items-center justify-between mb-3">
              <TrendingUp className="text-[#0B7C5C]" size={20} />
              <span className="text-xs uppercase tracking-[0.15em] text-[#5A6472]">Incasso Totale</span>
            </div>
            <p className="text-3xl font-mono font-bold text-[#0B7C5C]">{fmtEuro(summary.financials.revenue)}</p>
            <p className="text-xs text-[#5A6472] mt-2">
              Vendite: {fmtEuro(summary.financials.revenue_manual)} · Conto vendita: {fmtEuro(summary.financials.revenue_consignment)}
            </p>
          </div>
          <div className={`p-6 border ${summary.financials.net >= 0 ? "bg-[#0B192C] text-[#F9F8F6] border-[#0B192C]" : "bg-[#B23A48] text-[#F9F8F6] border-[#B23A48]"}`} data-testid="fin-net">
            <div className="flex items-center justify-between mb-3">
              <Sparkles size={20} />
              <span className="text-xs uppercase tracking-[0.15em] opacity-80">Utile Netto</span>
            </div>
            <p className="text-3xl font-mono font-bold">{fmtEuro(summary.financials.net)}</p>
            <p className="text-xs opacity-80 mt-2">
              Prezzo libro: {fmtEuro(summary.financials.book_price)} · Fee Amazon: -{fmtEuro(summary.financials.amazon_fee)}/copia
            </p>
          </div>
        </div>
      </section>

      {/* Charts */}
      <section className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-[#E5E1DA] p-6">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#5A6472] mb-4">Andamento mensile</h3>
          {summary.series.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={summary.series}>
                <CartesianGrid stroke="#F4EDE0" />
                <XAxis dataKey="month" stroke="#5A6472" style={{ fontSize: 11 }} />
                <YAxis yAxisId="left" stroke="#5A6472" style={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke="#5A6472" style={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: "#0B192C", border: "none", color: "#F9F8F6" }}
                  formatter={(v, k) => (typeof v === 'number' && (k === 'cost' || k === 'revenue' || k === 'net')) ? `€ ${v.toFixed(2)}` : v}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="revenue" fill="#0B7C5C" name="Incasso €" />
                <Bar yAxisId="left" dataKey="cost" fill="#B23A48" name="Spesa €" />
                <Line yAxisId="right" type="monotone" dataKey="sold" stroke="#0E7C8F" strokeWidth={2} name="Libri venduti" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#5A6472] text-center py-16">Nessun dato — inserisci il primo movimento qui sotto.</p>
          )}
        </div>
        <div className="bg-white border border-[#E5E1DA] p-6">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#5A6472] mb-4">Distribuzione libri</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={80} label>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0B192C", border: "none", color: "#F9F8F6" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#5A6472] text-center py-16">Nessun dato disponibile.</p>
          )}
        </div>
      </section>

      {/* Manual entry form */}
      <section className="bg-white border border-[#E5E1DA] p-6" data-testid="ledger-form-section">
        <h3 className="font-serif-display text-xl text-[#0B192C] mb-4">Aggiungi movimento manuale</h3>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Tipo</label>
            <select
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value})}
              className="w-full px-3 py-2 border border-[#E5E1DA] outline-none"
              data-testid="ledger-form-type"
            >
              {ENTRY_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Quantità</label>
            <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})}
              className="w-full px-3 py-2 border border-[#E5E1DA] outline-none" data-testid="ledger-form-qty" />
          </div>
          {(form.type === "sale_amazon" || form.type === "sale_direct") && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Prezzo unit. €</label>
              <input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({...form, unit_price: e.target.value})}
                placeholder="19.90"
                className="w-full px-3 py-2 border border-[#E5E1DA] outline-none" data-testid="ledger-form-price" />
            </div>
          )}
          {(form.type === "purchase" || form.type === "gift") && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Costo unit. €</label>
              <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({...form, unit_cost: e.target.value})}
                placeholder="4.50"
                className="w-full px-3 py-2 border border-[#E5E1DA] outline-none" data-testid="ledger-form-cost" />
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Data</label>
            <input type="date" value={form.entry_date} onChange={(e) => setForm({...form, entry_date: e.target.value})}
              className="w-full px-3 py-2 border border-[#E5E1DA] outline-none" data-testid="ledger-form-date" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-[10px] uppercase tracking-[0.15em] text-[#5A6472] mb-1">Note</label>
            <input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="Es. Regalato a giornalista La Sicilia; venduto evento Pula..."
              className="w-full px-3 py-2 border border-[#E5E1DA] outline-none" data-testid="ledger-form-notes" />
          </div>
          <div className="md:col-span-6 flex justify-end">
            <button type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.15em] hover:bg-[#1E3557] disabled:opacity-60"
              data-testid="ledger-form-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Aggiungi
            </button>
          </div>
        </form>
      </section>

      {/* Entries list */}
      {entries.length > 0 && (
        <section className="bg-white border border-[#E5E1DA]" data-testid="ledger-entries-list">
          <h3 className="p-4 font-serif-display text-lg text-[#0B192C] border-b border-[#E5E1DA]">
            Movimenti ({entries.length}) — <span className="text-sm text-[#5A6472]">clicca sull&apos;icona matita per modificare</span>
          </h3>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-[#5A6472] bg-[#F9F8F6]">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-center px-4 py-2">Qtà</th>
                <th className="text-right px-4 py-2">Prezzo/Costo unit.</th>
                <th className="text-right px-4 py-2">Spesa</th>
                <th className="text-right px-4 py-2">Incasso</th>
                <th className="text-left px-4 py-2">Note</th>
                <th className="w-24 text-center px-2 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(en => (
                <EntryRow
                  key={en.id}
                  entry={en}
                  onSave={updateEntry}
                  onDelete={remove}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Strategy panel */}
      <section data-testid="strategy-panel">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="text-[#D4B895]" />
          <h2 className="font-serif-display text-2xl text-[#0B192C]">
            Strategia · Suggerimenti per vendere di più
          </h2>
        </div>
        <p className="text-sm text-[#5A6472] mb-6 max-w-3xl">
          Consigli generati in base ai tuoi dati attuali. Il libro non è il prodotto finale — è
          l&apos;ingresso di un funnel che porta a certificazione, consulenze e corsi.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {summary.strategies.map((s, i) => {
            const p = PRIORITY[s.priority] || PRIORITY.low;
            const Ic = p.icon;
            return (
              <div key={i} className="bg-white border-l-4 border border-[#E5E1DA] p-5" style={{borderLeftColor: p.color}} data-testid={`strategy-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Ic size={14} style={{color: p.color}} />
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{color: p.color}}>
                    {s.category} · {p.label}
                  </span>
                </div>
                <h4 className="font-semibold text-[#0B192C] mb-2">{s.title}</h4>
                <p className="text-sm text-[#5A6472] whitespace-pre-line leading-relaxed">{s.detail}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, sublabel, icon: Icon, color, testid }) {
  return (
    <div className="bg-white border border-[#E5E1DA] p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} style={{ color }} />
        <span className="text-[10px] uppercase tracking-[0.15em] text-[#5A6472]">{label}</span>
      </div>
      <p className="text-4xl font-mono font-bold text-[#0B192C]">{value}</p>
      {sublabel && <p className="text-xs text-[#5A6472] mt-2">{sublabel}</p>}
    </div>
  );
}

function EntryRow({ entry, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    type: entry.type,
    quantity: entry.quantity,
    unit_price: entry.unit_price ?? "",
    unit_cost: entry.unit_cost ?? "",
    entry_date: entry.entry_date || "",
    notes: entry.notes || "",
  });

  const type = ENTRY_TYPES.find(t => t.id === entry.type);
  const showPrice = draft.type === "sale_amazon" || draft.type === "sale_direct";
  const showCost = draft.type === "purchase" || draft.type === "gift";

  const save = async () => {
    setSaving(true);
    const patch = {
      type: draft.type,
      quantity: Number(draft.quantity),
      entry_date: draft.entry_date || null,
      notes: draft.notes || "",
    };
    if (showPrice) patch.unit_price = draft.unit_price === "" ? 0 : Number(draft.unit_price);
    if (showCost) patch.unit_cost = draft.unit_cost === "" ? 0 : Number(draft.unit_cost);
    const ok = await onSave(entry.id, patch);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const cancel = () => {
    setDraft({
      type: entry.type,
      quantity: entry.quantity,
      unit_price: entry.unit_price ?? "",
      unit_cost: entry.unit_cost ?? "",
      entry_date: entry.entry_date || "",
      notes: entry.notes || "",
    });
    setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-t border-[#E5E1DA] bg-[#F4EDE0]/40" data-testid={`ledger-row-edit-${entry.id}`}>
        <td className="px-2 py-2">
          <input
            type="date"
            value={draft.entry_date}
            onChange={(e) => setDraft({...draft, entry_date: e.target.value})}
            className="w-full px-2 py-1 border border-[#E5E1DA] outline-none text-xs"
            data-testid={`edit-date-${entry.id}`}
          />
        </td>
        <td className="px-2 py-2">
          <select
            value={draft.type}
            onChange={(e) => setDraft({...draft, type: e.target.value})}
            className="w-full px-2 py-1 border border-[#E5E1DA] outline-none text-xs"
            data-testid={`edit-type-${entry.id}`}
          >
            {ENTRY_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </td>
        <td className="px-2 py-2 text-center">
          <input
            type="number"
            min="1"
            value={draft.quantity}
            onChange={(e) => setDraft({...draft, quantity: e.target.value})}
            className="w-20 px-2 py-1 border border-[#E5E1DA] outline-none text-center"
            data-testid={`edit-qty-${entry.id}`}
          />
        </td>
        <td className="px-2 py-2 text-right">
          {showPrice ? (
            <input
              type="number"
              step="0.01"
              placeholder="Prezzo €"
              value={draft.unit_price}
              onChange={(e) => setDraft({...draft, unit_price: e.target.value})}
              className="w-24 px-2 py-1 border border-[#E5E1DA] outline-none text-right"
              data-testid={`edit-price-${entry.id}`}
            />
          ) : showCost ? (
            <input
              type="number"
              step="0.01"
              placeholder="Costo €"
              value={draft.unit_cost}
              onChange={(e) => setDraft({...draft, unit_cost: e.target.value})}
              className="w-24 px-2 py-1 border border-[#E5E1DA] outline-none text-right"
              data-testid={`edit-cost-${entry.id}`}
            />
          ) : "—"}
        </td>
        <td colSpan={2} className="px-2 py-2 text-right text-xs text-[#5A6472] italic">Ricalcolato al salvataggio</td>
        <td className="px-2 py-2">
          <input
            value={draft.notes}
            onChange={(e) => setDraft({...draft, notes: e.target.value})}
            className="w-full px-2 py-1 border border-[#E5E1DA] outline-none text-xs"
            data-testid={`edit-notes-${entry.id}`}
          />
        </td>
        <td className="px-2 py-2 text-center whitespace-nowrap">
          <button
            onClick={save}
            disabled={saving}
            className="p-1.5 bg-[#0B7C5C] text-white mr-1 hover:bg-[#0B192C] disabled:opacity-50"
            data-testid={`edit-save-${entry.id}`}
            title="Salva"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          </button>
          <button
            onClick={cancel}
            className="p-1.5 bg-[#5A6472] text-white hover:bg-[#0B192C]"
            data-testid={`edit-cancel-${entry.id}`}
            title="Annulla"
          >
            <X size={12} />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-[#E5E1DA] hover:bg-[#F9F8F6]" data-testid={`ledger-row-${entry.id}`}>
      <td className="px-4 py-2 font-mono text-xs">{fmtDate(entry.entry_date)}</td>
      <td className="px-4 py-2">
        <span className="text-xs" style={{color: type?.color}}>■</span> {type?.label || entry.type}
      </td>
      <td className="px-4 py-2 text-center font-semibold">{entry.quantity}</td>
      <td className="px-4 py-2 text-right font-mono text-xs text-[#5A6472]">
        {entry.unit_price != null ? `€ ${Number(entry.unit_price).toFixed(2)}` : entry.unit_cost != null ? `€ ${Number(entry.unit_cost).toFixed(2)}` : "—"}
      </td>
      <td className="px-4 py-2 text-right font-mono text-[#B23A48]">{entry.cost ? fmtEuro(entry.cost) : "—"}</td>
      <td className="px-4 py-2 text-right font-mono text-[#0B7C5C]">{entry.revenue ? fmtEuro(entry.revenue) : "—"}</td>
      <td className="px-4 py-2 text-xs text-[#5A6472]">{entry.notes || "—"}</td>
      <td className="px-4 py-2 text-center whitespace-nowrap">
        <button
          onClick={() => setEditing(true)}
          className="p-1.5 border border-[#0E7C8F] text-[#0E7C8F] hover:bg-[#0E7C8F] hover:text-white mr-1"
          data-testid={`ledger-edit-${entry.id}`}
          title="Modifica"
        >
          <Pencil size={12} />
        </button>
        <button
          onClick={() => onDelete(entry.id)}
          className="p-1.5 border border-[#B23A48] text-[#B23A48] hover:bg-[#B23A48] hover:text-white"
          data-testid={`ledger-del-${entry.id}`}
          title="Elimina"
        >
          <Trash2 size={12} />
        </button>
      </td>
    </tr>
  );
}
